import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';
import {
  versionInstallee,
  fetchVersionPolicy,
  lirePolitiqueEnCache,
  ecrirePolitiqueEnCache,
  lireBanniereIgnoree,
  ecrireBanniereIgnoree,
} from '../services/appVersion';
import { evaluerVersion, type Verdict, type VersionPolicy } from '../utils/versionGate';

/**
 * Invite à la mise à jour, à deux niveaux : bannière ignorable et écran bloquant.
 *
 * FRÉQUENCE — au montage ET au retour au premier plan passé une heure. Une application mobile reste
 * ouverte des jours en arrière-plan : vérifier au seul démarrage rendrait le coupe-circuit inopérant
 * sur les appareils qui ne redémarrent jamais l'app, c'est-à-dire la majorité.
 */
export const INTERVALLE_REVERIFICATION_MS = 60 * 60 * 1000;

/**
 * LE GATE N'A PAS DE SENS SUR LE WEB, et ce n'est pas une optimisation.
 *
 * Il compare la version INSTALLÉE à celle publiée sur les stores. Sur le web, l'utilisateur
 * exécute le code qui vient d'être servi : il a la dernière version par construction, il n'y a
 * aucun store où l'envoyer, et « mettre à jour » ne veut rien dire.
 *
 * Laissé actif, il interrogeait `app_versions` avec `platform=eq.web` — une ligne qui n'existe pas
 * et NE DOIT PAS EXISTER. En ajouter une donnerait à une table qui peut BLOQUER l'application un
 * pouvoir sur une plateforme où le blocage n'a aucun sens : une erreur de saisie dans
 * `minimum_version` y afficherait un écran bloquant sans issue, puisque aucun bouton ne peut mener
 * à un store.
 *
 * La garde est donc posée ici, à la source : aucune requête, aucun écouteur `AppState`, et un
 * verdict `inconnu` — exactement l'état dans lequel le gate se met déjà quand il ne sait rien.
 * Le chemin de secours existant fait le reste, sans qu'aucun appelant ait à connaître la
 * plateforme.
 */
const GATE_ACTIF = Platform.OS !== 'web';

export interface VersionGate {
  verdict: Verdict;
  /** Destination du bouton de mise à jour. `null` tant qu'aucune politique n'est connue. */
  storeUrl: string | null;
  banniereVisible: boolean;
  ignorerBanniere: () => void;
}

export function useVersionGate(): VersionGate {
  const [politique, setPolitique] = useState<VersionPolicy | null>(null);
  const [ignoree, setIgnoree] = useState<string | null>(null);

  /**
   * Horodatage de la dernière lecture RÉUSSIE. Un échec ne l'avance pas : c'est justement quand la
   * lecture échoue qu'il faut réessayer tôt, et non se verrouiller une heure sur une panne passagère.
   */
  const derniereReussite = useRef(0);

  const verifier = useCallback(async () => {
    if (!GATE_ACTIF) return;
    try {
      const fraiche = await fetchVersionPolicy();
      if (fraiche) {
        setPolitique(fraiche);
        void ecrirePolitiqueEnCache(fraiche);
      }
      derniereReussite.current = Date.now();
    } catch {
      // FAIL-OPEN. Sans politique fraîche, on garde la dernière connue (cache) ou aucune — jamais un
      // blocage déduit d'une absence de réponse.
    }
  }, []);

  useEffect(() => {
    if (!GATE_ACTIF) return;
    let vivant = true;

    // Le cache d'abord : il donne un verdict immédiat au lancement, y compris hors ligne. Il ne
    // doit pas écraser une réponse réseau déjà arrivée, d'où le `ancienne ?? …`.
    void (async () => {
      const [enCache, rejetee] = await Promise.all([lirePolitiqueEnCache(), lireBanniereIgnoree()]);
      if (!vivant) return;
      if (enCache) setPolitique((ancienne) => ancienne ?? enCache);
      setIgnoree(rejetee);
    })();

    void verifier();

    const sub = AppState.addEventListener('change', (etat) => {
      if (etat !== 'active') return;
      if (Date.now() - derniereReussite.current < INTERVALLE_REVERIFICATION_MS) return;
      void verifier();
    });

    return () => {
      vivant = false;
      sub.remove();
    };
  }, [verifier]);

  const ignorerBanniere = useCallback(() => {
    const cible = politique?.latestVersion;
    if (!cible) return;
    setIgnoree(cible);
    void ecrireBanniereIgnoree(cible);
  }, [politique]);

  // APRÈS tous les hooks, jamais avant : leur ordre d'appel doit rester le même à chaque rendu.
  // `versionInstallee()` n'est même pas évaluée sur le web.
  if (!GATE_ACTIF) return { verdict: 'inconnu', storeUrl: null, banniereVisible: false, ignorerBanniere };

  const verdict = evaluerVersion(versionInstallee(), politique);

  // Un blocage ne s'ignore pas : le rejet mémorisé ne vaut que pour la bannière. Sans cette
  // condition, un utilisateur ayant fermé une bannière hier traverserait le blocage d'aujourd'hui.
  const banniereVisible = verdict === 'maj-disponible' && ignoree !== politique?.latestVersion;

  return { verdict, storeUrl: politique?.storeUrl ?? null, banniereVisible, ignorerBanniere };
}
